import { Controller } from "@hotwired/stimulus";
import { StytchUIClient } from "@stytch/vanilla-js";
import * as Sentry from "@sentry/browser";
import ToastService from "../../shared/toast_service";

export default class extends Controller {
  static values = {
    publicToken: String,
    errorMessage: {
      type: String,
      default:
        "Oops, something went wrong, we have been notified and will fix it soon.",
    },
    emailLoginStartMessage: {
      type: String,
      default: "Sending you a magic link, please wait...",
    },
    emailLoginEndMessage: {
      type: String,
      default:
        "We have sent you a magic link to sign in. Please check your email!",
    },
    googleStartMessage: {
      type: String,
      default: "Redirecting you to Google, please wait...",
    },
    facebookStartMessage: {
      type: String,
      default: "Redirecting you to Facebook, please wait...",
    },
    appleStartMessage: {
      type: String,
      default: "Redirecting you to Apple, please wait...",
    },
    locale: {
      type: String,
      default: "en",
    },
    authenticationUrl: String,
  };

  static targets = [
    "emailField",
    "emailInput",
    "submitButton",
    "successAlert",
    "errorAlert",
  ];

  connect() {
    if (!this.publicTokenValue) {
      throw new Error("Stytch public token is not set");
    }

    this.stytch = new StytchUIClient(this.publicTokenValue);
  }

  showEmailField() {
    this.emailFieldTarget.classList.remove("hidden");
    this.emailFieldTarget.classList.add("opacity-100", "translate-y-0");
    this.emailInputTarget.focus();
  }

  handleEmailChange() {
    // If the email has changed, hide the success alert and enable the button
    this.successAlertTarget.classList.add("hidden");
    this.errorAlertTarget.classList.add("hidden");
    this.submitButtonTarget.disabled = false;
  }

  async startEmailLogin(event) {
    event.preventDefault();
    const email = this.emailInputTarget.value;
    if (!email) return;

    try {
      // Disable the button while sending
      this.submitButtonTarget.disabled = true;
      this.errorAlertTarget.classList.add("hidden");

      new ToastService().success({
        text: this.emailLoginStartMessageValue,
      });

      await this.stytch.magicLinks.email.loginOrCreate(email, {
        login_magic_link_url: `${this.authenticationUrlValue}${window.location.pathname}${window.location.search}`,
        login_expiration_minutes: 60,
        signup_magic_link_url: `${this.authenticationUrlValue}${window.location.pathname}${window.location.search}`,
        signup_expiration_minutes: 60,
        locale: this.localeValue,
      });

      // Show success alert
      this.successAlertTarget.classList.remove("hidden");
      new ToastService().success({
        text: this.emailLoginEndMessageValue,
        // 10 seconds
        duration: 10000,
      });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);

      // Re-enable the button on error
      this.submitButtonTarget.disabled = false;

      // Show error alert
      this.errorAlertTarget.classList.remove("hidden");
      new ToastService().alert({
        text: this.errorMessageValue,
      });
    }
  }

  startGoogleOauth = async (event) => {
    event.target.disabled = true;

    new ToastService().toast({
      text: this.googleStartMessageValue,
    });

    try {
      this.stytch.oauth.google.start({
        login_redirect_url: `${this.authenticationUrlValue}${window.location.pathname}${window.location.search}`,
        signup_redirect_url: `${this.authenticationUrlValue}${window.location.pathname}${window.location.search}`,
      });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);

      // Re-enable the button on error
      event.target.disabled = false;

      // Show error alert
      this.errorAlertTarget.classList.remove("hidden");
      new ToastService().alert({
        text: this.errorMessageValue,
      });
    }
  };

  startFacebookOauth = async (event) => {
    event.target.disabled = true;

    new ToastService().toast({
      text: this.facebookStartMessageValue,
    });

    try {
      await this.stytch.oauth.facebook.start({
        login_redirect_url: `${this.authenticationUrlValue}${window.location.pathname}${window.location.search}`,
        signup_redirect_url: `${this.authenticationUrlValue}${window.location.pathname}${window.location.search}`,
      });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);

      // Re-enable the button on error
      event.target.disabled = false;

      // Show error alert
      this.errorAlertTarget.classList.remove("hidden");
      new ToastService().alert({
        text: this.errorMessageValue,
      });
    }
  };

  startAppleOauth = async (event) => {
    // Don't disable for Apple because they might cancel it and there is no way to re-enable it.
    // event.target.disabled = true;

    new ToastService().toast({
      text: this.appleStartMessageValue,
    });

    try {
      await this.stytch.oauth.apple.start({
        login_redirect_url: `${this.authenticationUrlValue}${window.location.pathname}${window.location.search}`,
        signup_redirect_url: `${this.authenticationUrlValue}${window.location.pathname}${window.location.search}`,
      });
    } catch (error) {
      console.error(error);
      Sentry.captureException(error);

      // Re-enable the button on error
      event.target.disabled = false;

      // Show error alert
      this.errorAlertTarget.classList.remove("hidden");
      new ToastService().alert({
        text: this.errorMessageValue,
      });
    }
  };
}
